import React from 'react';
import { Stack, Typography } from '@mui/material';
import {
  EmptyState, ListTable, Modal, PageTabs, Panel, StatusChip, ToolbarButton, type ListColumn,
} from '../../../ui-kit';
import {
  approveCwGuide, decideCwChange, fetchCwGuides, type CwGuide, type CwGuideChange, type CwGuideField,
} from '../../../services/carrierWebsiteService';
import { useLive, when } from '../rocco/live';

const WHY: Record<string, string> = {
  new_field: 'A field we did not know',
  new_button: 'A different way forward',
  person_changed: 'A person changed this on the page',
  person_fact: 'A person put a different fact here',
  person_format: 'A person wrote it in another form',
};

const CHANGES = '__changes';

function chipOf(guide: CwGuide) {
  if (guide.status === 'approved') return { tone: 'good' as const, label: `Approved · version ${guide.version}` };
  if (guide.status === 'draft') return { tone: 'warning' as const, label: 'Draft, needs approval' };
  return { tone: 'neutral' as const, label: 'Not visited yet' };
}

/** One side of a proposed change, in words. */
function side(value: Record<string, unknown> | null): string {
  if (!value) return 'Nothing yet';
  const { label, fact, ask, skip, was, now, next } = value as Record<string, string>;
  if (now) return `${label ? `${label}: ` : ''}${now}`;
  if (was) return `${label ? `${label}: ` : ''}${was}`;
  if (next) return `Press ${next}`;
  if (skip) return `${label}: left empty`;
  if (ask) return `${label}: ask a person (${ask})`;
  return `${label}: ${fact}`;
}

/** Every carrier, one row each. A row opens what we know about its website. */
export default function GuidesPage() {
  const { data, error, loading, reload } = useLive(fetchCwGuides, 8000);
  const [openKey, setOpenKey] = React.useState<string | null>(null);
  const guides = data?.guides ?? [];
  const open = guides.find((g) => g.carrier === openKey) ?? null;

  const columns: ListColumn<CwGuide>[] = [
    {
      key: 'name', header: 'Carrier', grow: 1, minWidth: 180, sortValue: (g) => g.name,
      render: (g) => <Typography variant="body2" sx={(t) => ({ color: t.tokens.text.primary })}>{g.name}</Typography>,
    },
    { key: 'status', header: 'Guide', width: 220, render: (g) => <StatusChip {...chipOf(g)} /> },
    {
      key: 'screens', header: 'Screens', width: 100, sortValue: (g) => g.screens.length,
      render: (g) => <Typography variant="body2" sx={(t) => ({ color: t.tokens.text.secondary })}>{g.screens.length}</Typography>,
    },
    {
      key: 'fields', header: 'Fields', width: 100, sortValue: (g) => g.screens.reduce((n, s) => n + s.fields.length, 0),
      render: (g) => (
        <Typography variant="body2" sx={(t) => ({ color: t.tokens.text.secondary })}>
          {g.screens.reduce((n, s) => n + s.fields.length, 0)}
        </Typography>
      ),
    },
    {
      key: 'proposed', header: 'Waiting for approval', width: 180, sortValue: (g) => g.proposed, sortStartsDescending: true,
      render: (g) => (g.proposed
        ? <StatusChip tone="warning" label={`${g.proposed} change${g.proposed === 1 ? '' : 's'}`} />
        : <Typography variant="body2" sx={(t) => ({ color: t.tokens.text.tertiary })}>None</Typography>),
    },
    {
      key: 'approved', header: 'Approved', width: 200, sortValue: (g) => g.approved_at,
      render: (g) => (
        <Stack>
          <Typography variant="body2" sx={(t) => ({ color: t.tokens.text.primary })}>{g.approved_at ? when(g.approved_at) : '—'}</Typography>
          <Typography variant="caption" sx={(t) => ({ color: t.tokens.text.tertiary })}>{g.approved_by ?? ''}</Typography>
        </Stack>
      ),
    },
    {
      key: 'action', header: 'Action', width: 110, align: 'right',
      render: (g) => <ToolbarButton variant="ghost" onClick={() => setOpenKey(g.carrier)}>Open</ToolbarButton>,
    },
  ];

  if (error && !data) return <EmptyState message={error} />;
  return (
    <>
      <Panel title="Carrier guides"
        subtitle="What we know about each carrier's website: its screens, what goes in each field, and how to move on. Nothing changes without a person">
        <ListTable columns={columns} rows={guides} getRowKey={(g) => g.carrier} loading={loading && !data}
          onRowClick={(g) => setOpenKey(g.carrier)} emptyMessage="No carriers yet." />
      </Panel>
      {open ? <GuideWindow guide={open} onClose={() => setOpenKey(null)} refresh={reload} /> : null}
    </>
  );
}

/** One carrier's guide: a tab a screen, in the order the website shows them, and the changes waiting. */
function GuideWindow({ guide, onClose, refresh }: { guide: CwGuide; onClose: () => void; refresh: () => void }) {
  const proposed = guide.changes.filter((c) => c.status === 'proposed');
  const tabs = [
    ...(proposed.length ? [{ value: CHANGES, label: `Waiting for approval (${proposed.length})` }] : []),
    ...guide.screens.map((s) => ({ value: s.key, label: s.title })),
  ];
  const [tab, setTab] = React.useState<string>(tabs[0]?.value ?? '');
  const current = tabs.some((t) => t.value === tab) ? tab : tabs[0]?.value ?? '';
  const screen = guide.screens.find((s) => s.key === current);

  return (
    <Modal open onClose={onClose} size="lg" title={guide.name}
      subtitle={[chipOf(guide).label, guide.approved_by ? `approved by ${guide.approved_by}` : null,
        `${guide.screens.length} screens`].filter(Boolean).join(' · ')}
      actions={guide.status === 'draft' ? (
        <ToolbarButton variant="brand" onClick={() => approveCwGuide(guide.carrier).then(refresh)}>Approve this guide</ToolbarButton>
      ) : null}>
      {tabs.length ? (
        <Stack spacing={2}>
          <PageTabs tabs={tabs} value={current} onChange={setTab} ariaLabel={`${guide.name} screens`} />
          {current === CHANGES ? (
            <Stack spacing={1.5}>
              {proposed.map((c) => <Change key={c.id} change={c} refresh={refresh} />)}
            </Stack>
          ) : screen ? (
            <Stack spacing={1.5}>
              {screen.repeats ? (
                <Typography variant="body2" sx={(t) => ({ color: t.tokens.text.secondary })}>{screen.repeats}.</Typography>
              ) : null}
              <ListTable columns={FIELD_COLUMNS} rows={screen.fields} getRowKey={(f) => f.key}
                emptyMessage="No fields on this screen. We only press the button to move on." />
              <Typography variant="body2" sx={(t) => ({ color: t.tokens.text.secondary })}>
                {screen.next ? `Then we press "${screen.next}".` : 'We have not learned the button that moves on from here yet.'}
              </Typography>
            </Stack>
          ) : null}
        </Stack>
      ) : (
        <EmptyState message="We have not visited this website yet. The first run learns it and drafts a guide for you to approve." />
      )}
    </Modal>
  );
}

const FIELD_COLUMNS: ListColumn<CwGuideField>[] = [
  {
    key: 'label', header: 'On the website', width: 260,
    render: (f) => <Typography variant="body2" sx={(t) => ({ color: t.tokens.text.primary, overflowWrap: 'anywhere' })}>{f.label}</Typography>,
  },
  {
    key: 'fills', header: 'We put in', grow: 1, minWidth: 240,
    render: (f) => (
      <Stack sx={{ minWidth: 0 }}>
        <Typography variant="body2" sx={(t) => ({
          color: f.skip ? t.tokens.text.tertiary : f.ask ? t.tokens.semantic.warning : t.tokens.text.primary,
        })}>{f.fills}</Typography>
        {/* The field in the customer file it comes from: drivers.{n} means each driver in turn. */}
        {f.fact ? (
          <Typography variant="caption" sx={(t) => ({
            color: t.tokens.text.tertiary, fontFamily: t.typography.fontFamilyMono, overflowWrap: 'anywhere',
          })}>{f.fact}</Typography>
        ) : null}
      </Stack>
    ),
  },
  {
    key: 'how', header: 'How', grow: 1, minWidth: 220,
    render: (f) => (
      <Typography variant="body2" sx={(t) => ({ color: t.tokens.text.secondary, overflowWrap: 'anywhere' })}>{f.how ?? '—'}</Typography>
    ),
  },
];

function Change({ change, refresh }: { change: CwGuideChange; refresh: () => void }) {
  const decide = async (approve: boolean) => { await decideCwChange(change.id, approve); refresh(); };
  const field = (change.field ?? change.screen).split(' | ').filter(Boolean).pop() ?? change.screen;
  return (
    <Stack spacing={0.75} sx={(t) => ({ p: 1.5, border: `1px solid ${t.tokens.border.subtle}`, borderRadius: `${t.ds.radius.sm}px` })}>
      <Stack direction="row" spacing={1} alignItems="baseline" sx={{ flexWrap: 'wrap' }}>
        <Typography variant="subtitle2" sx={(t) => ({ color: t.tokens.text.primary })}>{WHY[change.why] ?? change.why}</Typography>
        <Typography variant="caption" sx={(t) => ({ color: t.tokens.text.tertiary, overflowWrap: 'anywhere' })}>
          {field} · {when(change.at)}
        </Typography>
      </Stack>
      <Typography variant="body2" sx={(t) => ({ color: t.tokens.text.secondary, overflowWrap: 'anywhere' })}>Now: {side(change.before)}</Typography>
      <Typography variant="body2" sx={(t) => ({ color: t.tokens.text.primary, overflowWrap: 'anywhere' })}>Suggested: {side(change.after)}</Typography>
      <Stack direction="row" spacing={1} sx={{ pt: 0.5 }}>
        <ToolbarButton variant="brand" onClick={() => decide(true)}>Use this from now on</ToolbarButton>
        <ToolbarButton variant="ghost" onClick={() => decide(false)}>Leave the guide as it is</ToolbarButton>
      </Stack>
    </Stack>
  );
}
